import { useQuery } from "@tanstack/react-query";
import { knowledgeApi } from "@/api/knowledge";
import { companiesApi } from "@/api/companies";
import { issuesApi } from "@/api/issues";
import { isUserCreatedTask } from "@/lib/autoTasks";
import { queryKeys } from "@/lib/queryKeys";

/**
 * Estado dos "primeiros passos" de uma company nova. Usado pela página
 * /comecar e pelo Shell (pra mostrar o atalho "Primeiros passos · X/4"
 * enquanto o setup não estiver completo).
 *
 * Tudo é derivado de queries que já existem (KBs + dashboard) — sem endpoint
 * novo. As queries são compartilhadas via React Query, então chamar esse hook
 * em mais de um lugar não duplica request.
 */

export type SetupStepKey =
  | "onboarding"
  | "escala"
  | "produto"
  | "visual"
  | "integracao"
  | "tarefa";

export interface SetupStep {
  key: SetupStepKey;
  done: boolean;
  /** Steps opcionais não contam pra "setup completo". */
  optional: boolean;
  /** Pré-requisito não atendido — o passo fica disponível mas avisado. */
  blockedBy?: SetupStepKey;
}

export interface SetupProgress {
  loading: boolean;
  steps: SetupStep[];
  /** Primeiro KB type=product (pra deep-link na aba "Identidade visual"). */
  firstProductId: string | null;
  requiredDone: number;
  requiredTotal: number;
  allRequiredDone: boolean;
  /** true se nem dá pra calcular ainda (sem company). */
  unknown: boolean;
}

export function useSetupProgress(
  companyId: string | null | undefined,
): SetupProgress {
  const onboardingQuery = useQuery({
    queryKey: companyId
      ? queryKeys.companies.knowledgeOnboarding(companyId)
      : ["kb-onboarding-none"],
    queryFn: () => knowledgeApi.getOnboarding(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const kbsQuery = useQuery({
    queryKey: companyId ? queryKeys.companies.knowledge(companyId) : ["kbs-none"],
    queryFn: () => knowledgeApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const dashQuery = useQuery({
    queryKey: ["companies", companyId, "dashboard"],
    queryFn: () => companiesApi.dashboard(companyId!),
    enabled: !!companyId,
    staleTime: 15_000,
  });
  // Lista de issues pra distinguir tarefa CRIADA PELO USUÁRIO das auto do escala.
  const issuesQuery = useQuery({
    queryKey: companyId ? ["companies", companyId, "issues", "setup"] : ["issues-none"],
    queryFn: () => issuesApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 15_000,
  });

  const loading =
    !!companyId &&
    (onboardingQuery.isLoading || kbsQuery.isLoading || dashQuery.isLoading || issuesQuery.isLoading);

  const kbs = kbsQuery.data ?? [];
  const products = kbs.filter((k) => k.type === "product");
  const firstProduct = products[0] ?? null;

  const hasOnboarding = !!onboardingQuery.data;
  const hasEscala = (dashQuery.data?.kpis?.escalaHistory?.length ?? 0) > 0;
  const hasProduct = products.length > 0;
  const hasIntegration = (dashQuery.data?.kpis?.connectedIntegrations ?? 0) > 0;
  // "tarefa" só conta se o USUÁRIO criou uma — as 3 tarefas "E" que o escala
  // dispara sozinho (benchmark/tendências/concorrentes) NÃO marcam o passo.
  const hasTask = (issuesQuery.data ?? []).some((i) => isUserCreatedTask(i.templateKey));

  // Ordem: ONBOARDING primeiro (é a base de tudo — o ESCALA e as pesquisas de
  // benchmark que ele dispara já saem com a camada do onboarding KB). Por isso
  // escala/produto/tarefa ficam bloqueados até o onboarding estar feito.
  const steps: SetupStep[] = [
    { key: "onboarding", done: hasOnboarding, optional: false },
    { key: "escala", done: hasEscala, optional: false, blockedBy: hasOnboarding ? undefined : "onboarding" },
    { key: "produto", done: hasProduct, optional: false, blockedBy: hasOnboarding ? undefined : "onboarding" },
    // "visual" removido — a identidade visual agora é configurada DENTRO do
    // onboarding (step MIV), então não é mais um passo separado.
    { key: "integracao", done: hasIntegration, optional: true },
    {
      key: "tarefa",
      done: hasTask,
      optional: false,
      blockedBy: hasOnboarding ? undefined : "onboarding",
    },
  ];

  const required = steps.filter((s) => !s.optional);
  const requiredDone = required.filter((s) => s.done).length;

  return {
    loading,
    steps,
    firstProductId: firstProduct?.id ?? null,
    requiredDone,
    requiredTotal: required.length,
    allRequiredDone: requiredDone === required.length && !loading && !!companyId,
    unknown: !companyId,
  };
}
